<template>
  <v-container
    class="mt-2 pt-2"
    fluid>
    <v-row>
      <v-col class="col-12 pr-0">
        <v-file-input
          v-if="!playlistCertificateBadge"
          v-model="badgeUpload"
          :clearable=false
          :rules="badgeRules"
          :disabled="isUpdating || restrictPlaylistAccess"
          filled
          dense
          accept="image/png, image/svg+xml"
          placeholder="Upload a certificate badge"
          label="Printable playlist badge"
          ref="playlistBadgeUploader"
          @change="handleCertificateUpload(badgeUpload, 'certificateBadge')"
          hide-details="auto"
          class="mb-4 file-input"
        ></v-file-input>
        <div
          v-else
          id="certificateBadgeContainer"
          class="max-width d-flex align-center justify-space-between">
          <v-list-item class="px-0">
            <v-list-item-avatar tile class="thumbnail-style ml-n4">
              <v-img
                contain
                :src="playlistCertificateBadge"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
                <v-list-item-subtitle class="pl-3 text-left">Certificate badge</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-btn
            icon
            aria-label="remove certificate badge"
            @click="removeItem('certificateBadge')"
            :disabled="isUpdating"
          >
            <v-icon medium>mdi-delete</v-icon>
          </v-btn>
        </div>
        <v-file-input
          v-if="!playlistCertificateSignature"
          v-model="signatureUpload"
          :clearable=false
          :rules="badgeRules"
          :disabled="isUpdating || restrictPlaylistAccess"
          filled
          dense
          accept="image/*"
          placeholder="Upload a certificate signature"
          label="Printable playlist signature"
          ref="playlistSignatureUploader"
          @change="handleCertificateUpload(signatureUpload, 'certificateSignature')"
          hide-details="auto"
          class="mb-4 file-input"
        ></v-file-input>
        <div
          v-else
          id="certificateSignatureContainer"
          class="max-width d-flex align-center justify-space-between">
          <v-list-item class="px-0">
            <v-list-item-avatar tile class="thumbnail-style ml-n4">
              <v-img
                contain
                :src="playlistCertificateSignature"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
                <v-list-item-subtitle class="pl-3 text-left">Certificate signature</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-btn
            icon
            aria-label="remove certificate signature"
            @click="removeItem('certificateSignature')"
            :disabled="isUpdating"
          >
            <v-icon medium>mdi-delete</v-icon>
          </v-btn>
        </div>
        <v-row
          id="certificateMessageRow"
          class="d-flex align-center justify-center my-4 ml-1">
          <v-textarea
            clearable
            label="Certificate message"
            placeholder="Enter a fine print message here."
            class="message-input mr-4"
            rows="1"
            auto-grow
            counter="500"
            row-height="15"
            v-model="messageText"
            :rules="messageRules"
            :disabled="isUpdating || restrictPlaylistAccess"
            @blur="submitMessage()"
            @click:clear="submitMessage(true, true)"
          ></v-textarea>
        </v-row>
        <v-btn
          id="previewCertificateButton"
          small
          light
          block
          :color="playlistColor"
          outlined
          :disabled="isUpdating"
          @keyup.enter="handleLoadCertificatePreview"
          @click="handleLoadCertificatePreview"
        >
          Preview Certificate
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';
import _ from 'lodash';

export default {
  name: 'PlaylistCertificateSettings',
  data: () => ({
    badgeUpload: null,
    certificateSettingsKey: 0,
    isSchoolUser: false,
    playlistCertificateAccess: false,
    messageText: '',
    originalMessage: '',
    playlistColor: '#2196F3',
    signatureUpload: null,
    badgeRules: [
      value => !value || value.size < 25000000 || 'File size should be less than 25 MB!',
      value => !value || value.type.includes('image/') || 'Only images are allowed!',
    ],
    messageRules: [
      value => !value || value.length < 500 || 'Messages should be no longer than 500 characters',
      value => !value || value.split(/\r?\n/).filter(segment => segment.trim().length > 0).length < 3 || 'Messages should be broken into no more than two segments',
    ],
  }),
  computed: {
    ...mapState([
      'certificateBadge',
      'certificateMessage',
      'certificateSignature',
      'courseHash',
      'isUpdating',
      'playlist',
      'user',
    ]),
    ...mapGetters([
      'restrictPlaylistAccess',
    ]),
    playlistCertificateSignature() {
      return _.get(this.playlist, 'certificateSignature');
    },
    playlistCertificateBadge() {
      return _.get(this.playlist, 'certificateBadge');
    },
    isMessageValid() {
      if (_.isNull(this.messageText)) return false;
      let segments = this.messageText.split(/\r?\n/);
      segments = segments.filter(segment => segment.trim().length > 0);
      return this.messageText.trim().length > 0 && this.messageText.length < 500 && segments.length < 3 && this.messageText !== this.originalMessage;
    },
  },
  methods: {
    ...mapMutations([
      'toggleSnackbar',
      'setCertificateBadge',
      'setCertificateMessage',
      'setCertificateSignature',
    ]),
    ...mapActions([
      'addCertifificateMessage',
      'createCertificateItem',
      'encodeUserId',
      'removeCertificateItem',
      'updatePlaylist',
    ]),
    getColor() {
      this.playlistColor = _.get(this, 'playlist.default_color') ? this.playlist.default_color : '#2196F3';
    },
    async handleCertificateUpload(file, type) {
      try {
        if (file) {
          await this.createCertificateItem({
            file,
            certificateFileType: type,
          });
        }
      } catch(e) {
        this.toggleSnackbar({
          text: 'Error uploading file.',
          color: 'error',
          timeout: 2500,
        });
      }
    },
    handleLoadCertificatePreview() {
      const routePath = this.$router.resolve({ name: 'Course Certificate', params: { certificateHash: 'test-url', courseHash: this.courseHash } });
      window.open(routePath.href, '_blank');
    },
    removeItem(key) {
      this.removeCertificateItem(key);
      if (key === 'certificateBadge') {
        this.setCertificateBadge(null);
        this.badgeUpload = null;
      } else if (key === 'certificateSignature') {
        this.setCertificateSignature(null);
        this.signatureUpload = null;
      } else {
        this.setCertificateMessage(null);
        this.messageText = '';
      }
    },
    submitMessage(clear = false, clearableIconClicked = false) {
      if (!this.isMessageValid && !clearableIconClicked) return;
      if (clear) this.messageText = '';
      this.addCertifificateMessage(this.messageText);
      this.originalMessage = this.messageText;
    },
    updatePlaylistSettings() {
      const { config } = this.playlist;
      config.certificateAccess = this.playlistCertificateAccess;
      this.updatePlaylist({
        config,
      });
    },
  },
  watch: {
    // eslint-disable-next-line
    'playlist.config.certificateAccess'() {
      this.certificateAccess = this.playlist.config.certificateAccess;
    },
  },
  async beforeMount() {
    this.isSchoolUser = _.get(this.user, 'schoolId', false);
    this.playlistCertificateAccess = _.get(this, 'playlist.config.certificateAccess', false);
    const [
      badge,
      signature,
      message,
    ] = [
      _.get(this, 'playlist.certificateBadge', null),
      _.get(this, 'playlist.certificateSignature', null),
      _.get(this, 'playlist.certificateMessage', null),
    ];
    if (badge) this.setCertificateBadge(badge);
    if (signature) this.setCertificateSignature(signature);
    if (message) {
      this.messageText = message;
      this.originalMessage = message;
      this.setCertificateMessage(message);
    }
  },
  mounted() {
    this.getColor();
  },
  updated() {
    this.getColor();
  },
};
</script>

<style lang="scss">
.max-width  {
  width: 100%;
}

.thumbnail-style {
  height: 36px !important;
  width: 64px !important;
  max-height: 36px !important;
  max-width: 64px !important;
}
</style>
